/* eslint-disable simple-import-sort/imports */
import useGatAdminConfigData from "../../../../../../controllers/use-gat-admin-config-data";

import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FilledInput,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid2,
	IconButton,
	InputLabel,
	ListItemText,
	MenuItem,
	OutlinedInput,
	Select,
	Stack,
	Switch,
	TextField,
	Typography,
	styled,
} from "@mui/material";
import { Field, Form, Formik, FormikHelpers } from "formik";
import React, { useEffect } from "react";
import * as Yup from "yup";
import NumberInput from "./number-input";
import { useDispatch } from "react-redux";
import {
	createTimeOffType,
	editTimeOffType,
} from "../../../../../../store/gat-admin-slice";
import LoadingButton from "../../../../../../../../design/wilya-loading-button";

const PaperPropsSelect = {
	PaperProps: {
		style: {
			maxHeight: 48 * 3 + 14,
			width: 250,
		},
	},
	anchorOrigin: {
		vertical: "bottom",
		horizontal: "left",
	},
	transformOrigin: {
		vertical: "top",
		horizontal: "left",
	},
};

export interface LeaveTypeFormValues {
	name: string;
	description: string;
	timeOffIncrementNumber: number | "";
	timeOffIncrementMetric: string;
	advancedNoticeNumber: number | "";
	advanceNoticeMetric: string;
	validFrom: string;
	locations: number[];
	isEnabledAcrossCompany: boolean;
	id?: number;
	locs: { id: number; name: string }[];
	autoApprovalEnabled: boolean;
	pairingEnabled: boolean;
	isNoLimit: boolean;
	availableHrs: number | "";
}

interface LeaveTypeFormProps {
	open: boolean;
	onClose: () => void;
	mode: "add" | "edit";
	initialData: LeaveTypeFormValues | null;
}

const validationSchema = Yup.object().shape({
	name: Yup.string().required("Name is required"),
	description: Yup.string().max(
		150,
		"Description must be 150 characters or less"
	),
	timeOffIncrementNumber: Yup.number()
		.required("Time Off Increment is required"),
	advancedNoticeNumber: Yup.number(),
	advanceNoticeMetric: Yup.string().required("Advance Notice is required"),
	isEnabledAcrossCompany: Yup.boolean(),
	locations: Yup.array().when("isEnabledAcrossCompany", {
		is: false,
		then: Yup.array().min(1, "At least one location is required"),
	}),
	isNoLimit: Yup.boolean(),
	availableHrs: Yup.number().when("isNoLimit", {
		is: false,
		then: Yup.number()
			.required("Default Available Hours is required")
			.min(0.01, "Must be greater than 0")
			.max(9999, "Maximum value is 9999"),
	}),
});

const LeaveTypeForm: React.FC<LeaveTypeFormProps> = ({
	open,
	onClose,
	mode = "add",
	initialData = null,
}) => {
	const {
		locations,
		createTimeOffTypeStatus,
		createTimeOffTypeErrorMessage,
		editTimeOffTypeStatus,
		editTimeOffTypeErrorMessage,
		companyDataValue,
	} = useGatAdminConfigData();
	const dispatch = useDispatch();

	const locs = locations.map((loc: { id: number }) => loc.id);

	const initialValues: LeaveTypeFormValues = {
		name: "",
		description: "",
		timeOffIncrementNumber: 0.25,
		timeOffIncrementMetric: "",
		advancedNoticeNumber: "",
		advanceNoticeMetric: "Hours",
		validFrom: "",
		isEnabledAcrossCompany: true,
		locations: locs,
		locs: [],
		autoApprovalEnabled: false,
		pairingEnabled: false,
		isNoLimit: false,
		availableHrs: 1,
		...initialData,
	};

	if (initialData) {
		const data = initialData as any;
		initialValues.timeOffIncrementNumber =
			data.incrementHours ?? initialData.timeOffIncrementNumber;
		initialValues.advancedNoticeNumber =
			data.applyBeforeHours ?? initialData.advancedNoticeNumber;
		initialValues.availableHrs =
			data.availableHrs === 9999 ? "" : (data.availableHrs ?? "");
	}

	const handleSubmit = (values: LeaveTypeFormValues) => {
		const updatedLocs = values.isEnabledAcrossCompany
			? locations
			: locations.filter((loc: { id: number; name: string }) =>
					values.locations.includes(loc.id)
      );
		const payload = {
			name: values.name,
			description: values.description,
			companyId: companyDataValue.id,
			incrementHours: Number(values.timeOffIncrementNumber),
			applyBeforeHours: Number(values.advancedNoticeNumber),
			isEnabledAcrossCompany: values.isEnabledAcrossCompany,
			enabledLocations: values.locations,
			advanceNoticeMetric: values.advanceNoticeMetric,
			locs: updatedLocs,
			autoApprovalEnabled: values.autoApprovalEnabled,
			pairingEnabled: values.pairingEnabled,
			isNoLimit: values.isNoLimit,
			availableHrs: values.isNoLimit ? 9999 : Number(values.availableHrs),
		};
		// console.log(values, payload);
		if (mode === "add") {
			dispatch(createTimeOffType(payload));
		} else {
			dispatch(editTimeOffType({ ...payload, id: initialData?.id }));
		}
	};


	useEffect(() => {
		if (
			createTimeOffTypeStatus === "fulfilled" ||
			editTimeOffTypeStatus === "fulfilled"
		) {
			onClose();
		}
	}, [createTimeOffTypeStatus, editTimeOffTypeStatus, onClose]);

	const isEditMode = mode === "edit";
	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
			<DialogTitle
				sx={{
					padding: "32px 32px 20px 32px",
				}}
			>
				{isEditMode ? "Edit Time Off Type" : "Add Time Off Type"}
			</DialogTitle>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ errors, isSubmitting, touched, values, dirty, setFieldValue }) => (
					<Form>
						<DialogContent sx={{ height: 550, overflowY: "auto", padding: "20px 32px" }}>
							<Grid2 container spacing={1} sx={{ flexGrow: 1 }}>
								<Grid2 size={{xs: 12}} mb={2}>
									<Field
										as={TextField}
										fullWidth
										name='name'
										label='Name'
										variant='filled'
										error={!!errors.name}
										helperText={errors.name}
									/>
								</Grid2>
								<Grid2  size={{xs: 12}}>
									<FormControl fullWidth error={!!errors.description}>
										<Field
											as={TextField}
											variant='filled'
											fullWidth
											name='description'
											label='Description'
											multiline
											rows={2}
											inputProps={{ maxLength: 150 }}
											error={!!errors.description}
										/>
										<FormHelperText
											error={!!errors.description}
											style={{
												textAlign: errors.description ? "left" : "right",
											}}
										>
											{errors.description || `${values.description.length}/150`}
										</FormHelperText>
									</FormControl>
								</Grid2>
								<Grid2 size={{xs: 8}} mb={1}>
									<Typography
										sx={{
											fontFamily: "Roboto",
											fontWeight: 400,
											fontSize: "16px",
											color: "rgba(0, 0, 0, 0.87)",
											lineHeight: "24px",
										}}
									>
										Advanced Notice Required
									</Typography>
								</Grid2>
								<Grid2 size={{xs: 4}}>
								<Typography
										sx={{
											fontFamily: "Roboto",
											fontWeight: 400,
											fontSize: "16px",
											color: "rgba(0, 0, 0, 0.87)",
											lineHeight: "24px",
											mb: 1,
										}}
									>
										Time Off Increment
									</Typography>
								</Grid2>
								<Grid2 size={{xs: 4}}>
									<FormControl fullWidth>
										<Field name='advancedNoticeNumber'>
											{({ field, form }: any) => (
												<NumberInput
													{...field}
													fullWidth
													label='Number'
													variant='filled'
													value={field.value || 0}
													onChange={(value: number) =>
														form.setFieldValue(field.name, value)
													}
													error={!!errors.advancedNoticeNumber}
													helperText={errors.advancedNoticeNumber}
													min={0}
													max={100} // Set an appropriate maximum value
												/>
											)}
										</Field>
									</FormControl>
								</Grid2>
								<Grid2 size={{xs: 4}}>
									<FormControl fullWidth>
										<Field
											label='Metric'
											component={TextField}
											type='text'
											select
											variant='filled'
											name='advanceNoticeMetric'
											id='advanceNoticeMetric'
											error={!!errors.advanceNoticeMetric}
											value={values.advanceNoticeMetric}
											onChange={(e: any) =>
												setFieldValue("advanceNoticeMetric", e.target.value)
											}
										>
											<MenuItem value='Hours'>Hours</MenuItem>
											<MenuItem value='Days'>Days</MenuItem>
										</Field>
									</FormControl>
								</Grid2>

								<Grid2 size={{xs: 4}}>
									<FormControl fullWidth>
										<Field
											label='Number (Metric)'
											component={TextField}
											type='number'
											select
											variant='filled'
											name='timeOffIncrementNumber'
											id='timeOffIncrementNumber'
											error={!!errors.timeOffIncrementNumber}
											value={values.timeOffIncrementNumber}
											onChange={(e: any) =>
												setFieldValue("timeOffIncrementNumber", e.target.value)
											}
										>
											<MenuItem value={0.25}>15 Minutes</MenuItem>
											<MenuItem value={0.5}>30 Minutes</MenuItem>
											<MenuItem value={1}>1 Hour</MenuItem>
											<MenuItem value={2}>2 Hours</MenuItem>
											<MenuItem value={4}>4 Hours</MenuItem>
										</Field>
									</FormControl>
								</Grid2>

								<Grid2 size={{ xs: 12 }} mt={2}>
									<FormControlLabel
										control={
											<Switch
												checked={values.isNoLimit}
												onChange={(event) => {
													setFieldValue("isNoLimit", event.target.checked);
												}}
												name='isNoLimit'
												color='primary'
											/>
										}
										label='No Limit'
									/>
								</Grid2>
								{!values.isNoLimit && (
									<Grid2 size={{ xs: 4 }}>
										<FormControl fullWidth>
											<Field name='availableHrs'>
												{({ field, form }: any) => (
													<NumberInput
														{...field}
														fullWidth
														label='Default Available Hours'
														variant='filled'
														value={field.value || 0}
														onChange={(value: number) =>
															form.setFieldValue(field.name, value)
														}
														error={!!errors.availableHrs}
														helperText={errors.availableHrs}
														min={0}
														max={9999}
													/>
												)}
											</Field>
										</FormControl>
									</Grid2>
								)}

								<Grid2 size={{xs:12}} mt={4}>
									<FormControlLabel
										control={
											<Switch
												checked={values.isEnabledAcrossCompany}
												onChange={(event) => {
													setFieldValue(
														"isEnabledAcrossCompany",
														event.target.checked
													);
													if (event.target.checked) {
														const locs = locations.map(
															(loc: { id: number }) => loc.id
														);
														setFieldValue("locations", locs);
													} else setFieldValue("locations", []);
												}}
												name='isEnabledAcrossCompany'
												color='primary'
											/>
										}
										label='Applies to all Locations in the Company'
									/>
									{!values.isEnabledAcrossCompany && (
										<FormControl
											fullWidth
											error={!!errors.locations && touched.locations}
											variant='filled'
											//sx={{ marginTop: "16px" }}
										>
											<InputLabel id='locations-label'>Locations*</InputLabel>
											<Field
												as={Select}
												labelId='locations-label'
												name='locations'
												multiple
												input={<FilledInput />}
												MenuProps={PaperPropsSelect}
												renderValue={(selected: number[]) => {
													return selected.length > 3
														? `${selected.length} Locations`
														: selected
																.map(
																	(id) =>
																		locations.find(
																			(loc: { id: number }) => loc.id === id
																		)?.name
																)
																.join(", ");
												}}
											>
												{locations.length === 0 ? (
													<MenuItem disabled value=''>
														No Locations Found
													</MenuItem>
												) : (
													locations.map(
														(location: { id: number; name: string }) => (
															<MenuItem key={location.id} value={location.id}>
																<Checkbox
																	checked={
																		values.locations.indexOf(location.id) > -1
																	}
																/>
																<ListItemText primary={location.name} />
															</MenuItem>
														)
													)
												)}
											</Field>
											{errors.locations && touched.locations && (
												<FormHelperText>{errors.locations}</FormHelperText>
											)}
										</FormControl>
									)}
								</Grid2>

								<Grid2 size={{xs:12}} mt={2}>
									<FormControlLabel
										control={
											<Switch
												checked={values.autoApprovalEnabled}
												onChange={(event) => {
													setFieldValue(
														"autoApprovalEnabled",
														event.target.checked
													);
												}}
												name='autoApprovalEnabled'
												color='primary'
											/>
										}
										label='Auto approve Worker requests if there is sufficient balance'
									/>
								</Grid2>

								<Grid2 size={{xs:12}} mt={2}>
									<FormControlLabel
										control={
											<Switch
												checked={values.pairingEnabled}
												onChange={(event) => {
													setFieldValue(
														"pairingEnabled",
														event.target.checked
													);
												}}
												name='pairingEnabled'
												color='primary'
											/>
										}
										label='Can be used together with other Time Off Types'
									/>
								</Grid2>
							</Grid2>
						</DialogContent>
						<DialogActions
							sx={{
								padding: "25px",
							}}
						>
							<Button onClick={onClose}>Cancel</Button>
							<LoadingButton
								type='submit'
								variant='contained'
								color='primary'
								disabled={
									Object.keys(errors).length > 0 ||
									!dirty ||
									values.locations.length === 0
								}
								loading={
									createTimeOffTypeStatus === "pending" ||
									editTimeOffTypeStatus === "pending"
								}
							>
								{isEditMode ? "Update" : "Add"}
							</LoadingButton>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default LeaveTypeForm;
